import { useEffect, useState } from 'preact/hooks';
import { Knot } from '../components/Knot';
import { Onboarding } from './Onboarding';
import { setStoredTenantId } from '../lib/tenant';
import { listTenants, ApiError, type ApiUser, type TenantMembership } from '../lib/api';
import './TenantResolver.css';

export interface TenantResolverProps {
  /** The signed-in user; forwarded to Onboarding when there's no workspace. */
  user: ApiUser;
  /** Carried through to Onboarding (the fresh-registration email-confirm note). */
  notice?: string;
  /** Called with the chosen/created tenantId once resolved; App stores it as
   *  active and renders the shell. */
  onResolved: (tenantId: string) => void;
}

type State =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'onboarding' }
  | { phase: 'picker'; tenants: TenantMembership[] };

// Auth-2: tenant resolution after auth, replacing the paste-an-id
// ConnectWorkspace stopgap. GET /auth/tenants -> exactly one auto-selects
// straight to Today (no id to paste, ever); none drops into first-run
// onboarding; several show a calm workspace picker. See lib/tenant.ts for how
// the chosen id then travels as X-Tenant-Id on every /api call.
export function TenantResolver({ user, notice, onResolved }: TenantResolverProps) {
  const [state, setState] = useState<State>({ phase: 'loading' });

  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      try {
        const tenants = await listTenants();
        if (cancelled) return;
        if (tenants.length === 1) {
          const only = tenants[0]!;
          setStoredTenantId(only.tenantId);
          onResolved(only.tenantId);
          return;
        }
        if (tenants.length === 0) {
          setState({ phase: 'onboarding' });
          return;
        }
        setState({ phase: 'picker', tenants });
      } catch (err) {
        if (cancelled) return;
        setState({
          phase: 'error',
          message: err instanceof ApiError ? err.message : 'Could not reach the server.',
        });
      }
    }

    void resolve();
    return () => {
      cancelled = true;
    };
    // Resolve once per mount — App unmounts this the moment a tenant is set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state.phase === 'loading') {
    return (
      <div class="tenant">
        <div class="tenant__center">
          <Knot size={46} />
          <div class="tenant__say tenant__say--tight">One moment…</div>
        </div>
      </div>
    );
  }

  if (state.phase === 'error') {
    return (
      <div class="tenant">
        <div class="tenant__center">
          <Knot size={46} />
          <div class="tenant__say tenant__say--tight">
            Couldn’t load your workspaces.
            <br />
            <span class="tenant__say-soft">{state.message}</span>
          </div>
        </div>
      </div>
    );
  }

  if (state.phase === 'onboarding') {
    return <Onboarding user={user} notice={notice} onResolved={onResolved} />;
  }

  // Multiple workspaces — a calm picker.
  function pick(tenantId: string) {
    setStoredTenantId(tenantId);
    onResolved(tenantId);
  }

  return (
    <div class="tenant">
      <div class="tenant__eyebrow">Your workspaces</div>
      <div class="tenant__say">
        Where to?
        <br />
        <span class="tenant__say-soft">Choose a workspace to open.</span>
      </div>
      <div class="tenant__list">
        {state.tenants.map((t) => (
          <button type="button" key={t.tenantId} class="tenant__item" onClick={() => pick(t.tenantId)}>
            <span class="tenant__item-name">{t.name}</span>
            <span class="tenant__item-role">{t.role}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
